import { Button } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import Dialog from 'components/Dialog';
import { useForm } from 'kineticUtils';
import { KineticForm } from 'components/forms';
import { useTranslation } from 'react-i18next';
import { useExternalLinkContext } from '../context';

type ExternalLinkFormFields = {
  url: string;
  tooltip: string;
};

export const AdminModal = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation('common');

  const { preference, setPreference, preferenceKey } = useExternalLinkContext() ?? {};

  const fieldConfigs = useMemo(
    () => [
      {
        id: 'url',
        name: 'url',
        label: t('url'),
        type: 'text',
        required: true,
      },
      {
        id: 'tooltip',
        name: 'tooltip',
        label: t('tooltip'),
        type: 'text',
        required: true,
      },
    ],
    [t]
  );

  const defaultValues = preferenceKey
    ? {
        url: preference?.[preferenceKey]?.[0]?.url || '',
        tooltip: preference?.[preferenceKey]?.[0]?.tooltip || '',
      }
    : {
        url: '',
        tooltip: '',
      };

  const {
    props: formProps,
    values: [values, setValues],
    dirty: [_, __, setDirtyFields],
    isSaveDisabled,
  } = useForm<ExternalLinkFormFields>({
    fields: fieldConfigs,
    defaultValues,
  });

  // Reset form when preferenceKey is deleted
  useEffect(() => {
    if (isOpen && preferenceKey) {
      setValues({
        url: preference?.[preferenceKey]?.[0]?.url || '',
        tooltip: preference?.[preferenceKey]?.[0]?.tooltip || '',
      });
      setDirtyFields([]);
    }
  }, [isOpen, preference, preferenceKey, setDirtyFields, setValues]);

  const handleSubmit = async () => {
    const formValues = values as ExternalLinkFormFields;
    if (!preferenceKey) {
      return;
    }

    setPreference({
      ...preference,
      [preferenceKey]: [
        {
          url: formValues.url,
          tooltip: formValues.tooltip,
        },
      ],
    });

    setIsOpen(false);
  };

  return (
    <Dialog
      open={isOpen}
      title={t('externalLinkSettings')}
      buttons={
        <>
          <Button onClick={() => setIsOpen(false)} variant='outlined'>
            {t('cancel')}
          </Button>
          <Button onClick={() => handleSubmit()} disabled={isSaveDisabled} variant='contained'>
            {t('save')}
          </Button>
        </>
      }
    >
      <KineticForm {...formProps} />
    </Dialog>
  );
};
